import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "stock-items";

export interface StockItem {
	id: string;
	code: string;
	name: string;
	description: string | null;
	category: string | null;
	uom: string | null;
	isActive: boolean;
	isInventoryItem: boolean;
	isSalesItem: boolean;
	isPurchaseItem: boolean;
	manageSerialNumbers: boolean;
	manageBatchNumbers: boolean;
	currentStock: string | null;
	reservedAmount: string | null;
	onOrderAmount: string | null;
	maxStock: string | null;
	minStock: string | null;
	reorderPoint: string | null;
	sapItemCode: string | null;
	sapItemGroup: number | null;
	sapSalesUnit: string | null;
	sapPurchaseUnit: string | null;
	defaultWarehouseCode?: string | null;
	lastSyncedAt: string | null;
	syncStatus: string | null;
	createdAt: string;
	updatedAt: string;
	totalStock?: string | null;
	totalCommitted?: string | null;
	totalIncoming?: string | null;
}

export type StockItemListRow = Omit<StockItem, "id"> & { id: string | null };

export interface StockItemWarehouse {
	id: string;
	warehouseId: string;
	warehouseCode: string | null;
	warehouseName: string | null;
	currentStock: string | null;
	reservedAmount: string | null;
	onOrderAmount: string | null;
	minStock: string | null;
	maxStock: string | null;
}

export function useStockItemList(options?: {
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	status?: string | undefined;
	category?: string | undefined;
	page?: number;
	limit?: number;
	enabled?: boolean;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			if (options?.status) params.set("status", options.status);
			if (options?.category) params.set("category", options.category);
			return api.get<{ items: StockItemListRow[]; total: number }>(
				`/api/stock-items?${params.toString()}`,
			);
		},
		enabled: options?.enabled ?? true,
	});
}

export function useStockItemDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () =>
			api.get<StockItem & { warehouseStocks: StockItemWarehouse[] }>(`/api/stock-items/${id}`),
		enabled: !!id,
	});
}

export function useStockItemCategories() {
	return useQuery({
		queryKey: [KEY, "categories"],
		queryFn: () =>
			api.get<{ categories: { category: string; code: number | null }[] }>(
				"/api/stock-items/categories",
			),
		staleTime: 5 * 60 * 1000,
	});
}

export function useStockItemUnits() {
	return useQuery({
		queryKey: [KEY, "units"],
		queryFn: () => api.get<{ units: string[] }>("/api/stock-items/units"),
		staleTime: 5 * 60 * 1000,
	});
}

export interface StockMovement {
	date: string;
	warehouseCode: string;
	document: string;
	counterpartName: string | null;
	quantity: number;
	price: number | null;
	currency: string | null;
}

export function useStockItemMovements(
	id: string | null,
	options?: { startDate?: string; endDate?: string },
) {
	return useQuery({
		queryKey: [KEY, "movements", id, options],
		queryFn: () => {
			const params = new URLSearchParams();
			if (options?.startDate) params.set("startDate", options.startDate);
			if (options?.endDate) params.set("endDate", options.endDate);
			return api.get<{ movements: StockMovement[] }>(
				`/api/stock-items/${id}/movements?${params.toString()}`,
			);
		},
		enabled: !!id,
	});
}

export interface StockItemOpenOrder {
	docEntry: number;
	docNum: number;
	docDate: string;
	itemCode: string;
	warehouse: string | null;
	viaItemCode: string | null;
	quantity: number;
}

export function useStockItemOpenOrders(id: string | null) {
	return useQuery({
		queryKey: [KEY, "open-orders", id],
		queryFn: () => api.get<{ orders: StockItemOpenOrder[] }>(`/api/stock-items/${id}/open-orders`),
		enabled: !!id,
		retry: false,
	});
}

export interface StockItemArrival {
	loadReportId: string;
	reservationNo: string | null;
	vesselName: string | null;
	arrivalPort: string | null;
	departureDate: string;
	estimatedArrivalDate: string;
	status: string;
	quantity: string;
}

export function useStockItemIncoming(id: string | null) {
	return useQuery({
		queryKey: [KEY, "incoming", id],
		queryFn: () => api.get<{ arrivals: StockItemArrival[] }>(`/api/stock-items/${id}/incoming`),
		enabled: !!id,
	});
}

export interface SerialWarehouse {
	itemCode: string;
	warehouseCode: string;
	warehouseName: string | null;
	serialNo: string;
	quantity: number;
}

export function useStockItemSerials(id: string | null) {
	return useQuery({
		queryKey: [KEY, "serials", id],
		queryFn: () => api.get<{ serials: SerialWarehouse[] }>(`/api/stock-items/${id}/serials`),
		enabled: !!id,
	});
}

export interface StockCategoryViewRow {
	id: string | null;
	itemCode: string;
	itemName: string;
	brand: string;
	eregli: number;
	usak: number;
	other: number;
	otherWarehouses: { whsCode: string; onHand: number }[];
	total: number;
	committed: number;
	committedViaSet: number;
	incoming: Record<string, number>;
	incomingTotal: number;
	openPurchase: number;
}

export interface StockItemOpenPurchase {
	docEntry: number;
	docNum: number;
	docDate: string;
	dueDate: string | null;
	openQty: number;
	loadedQty: number;
	unloadedQty: number;
}

export function useStockItemOpenPurchases(id: string | null) {
	return useQuery({
		queryKey: [KEY, "open-purchases", id],
		queryFn: () =>
			api.get<{ orders: StockItemOpenPurchase[] }>(`/api/stock-items/${id}/open-purchases`),
		enabled: !!id,
		retry: false,
	});
}

export function useStockCategoryView(category: string, includeEmpty: boolean) {
	return useQuery({
		queryKey: [KEY, "category-view", category, includeEmpty],
		queryFn: () => {
			const params = new URLSearchParams({ category });
			if (includeEmpty) params.set("includeEmpty", "true");
			return api.get<{
				rows: StockCategoryViewRow[];
				dates: string[];
				committedAvailable: boolean;
			}>(`/api/stock-items/category-view?${params.toString()}`);
		},
		retry: false,
	});
}
